import React from 'react';
import SEO from '../components/SEO';
import { Shield, Lock, Server, Eye, AlertTriangle, CheckCircle, MapPin } from 'lucide-react';

export default function TechnologySecurity() {
  return (
    <>
      <SEO
        title="Technology & Security | UK Data Residency | Cyber Essentials Plus"
        description="Comprehensive security architecture with UK-only data residency, ISO 27001 certification, and Cyber Essentials Plus compliance for UK public sector."
        keywords={[
          'UK Data Residency',
          'Cyber Essentials Plus',
          'ISO 27001',
          'Government Security',
          'ATRS Compliance',
          'Data Protection',
          'Security Architecture',
          'UK Datacentres'
        ]}
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-green-500/10 border border-green-500/30 rounded-full px-4 py-2 mb-6">
              <Shield className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm font-medium">UK-Only Data • Cyber Essentials Plus • ISO 27001</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Technology & Security
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Enterprise-grade security architecture designed specifically for UK public sector requirements.
              All data stays in the UK with comprehensive compliance and monitoring.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="bg-blue-500/20 p-3 rounded-lg">
                  <Server className="w-8 h-8 text-blue-400" />
                </div>
                <h2 className="text-2xl font-bold text-white">Infrastructure</h2>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-1">UK-Only Data Residency</h3>
                    <p className="text-gray-400 text-sm">London & Manchester datacentres exclusively</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-1">ISO 27001:2022 Certified ISMS</h3>
                    <p className="text-gray-400 text-sm">Information Security Management System (Latest Standard)</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-1">Cyber Essentials Plus</h3>
                    <p className="text-gray-400 text-sm">Government-mandated security certification</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-1">Penetration Testing</h3>
                    <p className="text-gray-400 text-sm">Quarterly by CREST-certified partners</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-1">24/7 Security Monitoring</h3>
                    <p className="text-gray-400 text-sm">Continuous threat detection and response</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="bg-green-500/20 p-3 rounded-lg">
                  <Lock className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-white">Data Protection</h2>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-1">Encryption Standards</h3>
                    <p className="text-gray-400 text-sm">AES-256 at rest, TLS 1.3 in transit</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-1">Multi-Factor Authentication</h3>
                    <p className="text-gray-400 text-sm">Mandatory for all system access</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-1">Audit Logging</h3>
                    <p className="text-gray-400 text-sm">7-year retention with immutable records</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-1">Data Protection Impact Assessments</h3>
                    <p className="text-gray-400 text-sm">Completed for all projects before deployment</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-1">Named Data Protection Officer</h3>
                    <p className="text-gray-400 text-sm">Available for consultation and compliance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8 mb-16">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-purple-500/20 p-3 rounded-lg">
                <Eye className="w-8 h-8 text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold text-white">Governance & Compliance</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <h3 className="font-semibold text-purple-400 mb-4">Security Incident Response</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Detection: Under 1 hour</li>
                  <li>• Initial response: Within 4 hours</li>
                  <li>• Client notification: Within 8 hours</li>
                  <li>• Full remediation plan: Within 24 hours</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-blue-400 mb-4">Business Continuity</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• 99.9% uptime SLA (43.8 minutes/month max)</li>
                  <li>• Recovery Time Objective (RTO): 4 hours</li>
                  <li>• Recovery Point Objective (RPO): 1 hour</li>
                  <li>• Quarterly disaster recovery testing</li>
                  <li>• Geographic redundancy across UK regions</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-green-400 mb-4">Compliance Certifications</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Cyber Essentials Plus</li>
                  <li>• ISO 27001:2022</li>
                  <li>• GDPR Article 28 compliant</li>
                  <li>• ICO registered</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8 mb-16">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-teal-500/20 p-3 rounded-lg">
                <Server className="w-8 h-8 text-teal-400" />
              </div>
              <h2 className="text-2xl font-bold text-white">Technology Stack</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-teal-400 mb-4">AI & Automation</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• OpenAI GPT-4/GPT-4V (via Azure UK)</li>
                  <li>• Anthropic Claude 3.5 Sonnet</li>
                  <li>• UiPath / Microsoft Power Automate</li>
                  <li>• Custom LangChain/LangGraph agents</li>
                  <li>• Document AI: Tesseract, AWS Textract (UK region)</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-blue-400 mb-4">Infrastructure</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Primary: Azure UK South (London) region</li>
                  <li>• Secondary: AWS eu-west-2 (London) region</li>
                  <li>• All compute and storage in UK-only regions</li>
                  <li>• No data leaves UK sovereignty boundary</li>
                  <li>• Kubernetes (CNCF certified) for orchestration</li>
                  <li>• Immutable infrastructure deployments</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-gray-800/80 to-gray-900/80 backdrop-blur-sm border border-orange-500/30 rounded-xl p-8">
            <div className="flex items-start space-x-4">
              <div className="bg-orange-500/20 p-3 rounded-lg flex-shrink-0">
                <AlertTriangle className="w-8 h-8 text-orange-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Transparency & Ethics</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-orange-400 mb-2">ATRS Compliance</h3>
                    <p className="text-gray-300 text-sm mb-3">
                      Algorithmic Transparency Recording Standard compliance for all AI systems used in central government.
                    </p>
                    <ul className="space-y-1 text-gray-400 text-sm">
                      <li>• Full algorithm documentation</li>
                      <li>• Decision-making transparency</li>
                      <li>• Regular bias audits</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-purple-400 mb-2">Explainable AI (XAI)</h3>
                    <p className="text-gray-300 text-sm mb-3">
                      All AI decisions can be explained in plain language for oversight and accountability.
                    </p>
                    <ul className="space-y-1 text-gray-400 text-sm">
                      <li>• Human-in-the-loop for critical decisions</li>
                      <li>• Bias monitoring and fairness testing</li>
                      <li>• Quarterly ethics reviews</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
